// ============================================================================
// USE PHASE CONFIG HOOK
// Manages journey phase configuration (artifacts and entities)
// ============================================================================

import { useState, useEffect, useCallback } from 'react'
import { useSessionStore } from '@/stores/sessionStore'
import { journeyApi, type JourneyFrameworkResponse } from '@/lib/api/client'
import type {
  ConfigurableItem,
  ConfigurableItemsResponse,
  PhaseArtifact,
  PhaseEntity,
  JourneyPhase,
} from '@ispot/shared'

// ============================================================================
// TYPES
// ============================================================================

export interface PhaseConfigState {
  framework: JourneyFrameworkResponse | null
  configurableItems: ConfigurableItemsResponse | null
  loading: boolean
  saving: boolean
  error: string | null
}

export interface PhaseConfigUpdate {
  artifacts: PhaseArtifact[]
  entities: PhaseEntity[]
}

// ============================================================================
// HOOK
// ============================================================================

export function usePhaseConfig() {
  const { accountId } = useSessionStore()
  const [state, setState] = useState<PhaseConfigState>({
    framework: null,
    configurableItems: null,
    loading: true,
    saving: false,
    error: null,
  })

  // Load framework and configurable items
  const loadData = useCallback(async () => {
    if (!accountId) {
      setState(prev => ({ ...prev, loading: false, error: 'No account selected' }))
      return
    }

    setState(prev => ({ ...prev, loading: true, error: null }))

    try {
      const [frameworkResponse, itemsResponse] = await Promise.all([
        journeyApi.getFramework(accountId),
        journeyApi.getConfigurableItems(accountId),
      ])

      setState({
        framework: frameworkResponse.framework,
        configurableItems: itemsResponse,
        loading: false,
        saving: false,
        error: null,
      })
    } catch (err) {
      setState(prev => ({
        ...prev,
        loading: false,
        error: err instanceof Error ? err.message : 'Failed to load phase config',
      }))
    }
  }, [accountId])

  // Update phase configuration
  const updatePhaseConfig = useCallback(
    async (phaseId: string, config: PhaseConfigUpdate) => {
      if (!accountId || !state.framework) {
        return { success: false, error: 'No account or framework' }
      }

      setState(prev => ({ ...prev, saving: true, error: null }))

      try {
        const response = await journeyApi.updatePhaseConfig(
          accountId,
          state.framework.id,
          phaseId,
          {
            artifacts: config.artifacts.map(a => ({
              id: a.id,
              name: a.name,
              type: a.type,
              templateId: a.templateId,
              isRequired: a.isRequired,
            })),
            entities: config.entities.map(e => ({
              classUri: e.classUri,
              label: e.label,
              minCount: e.minCount,
              isRequired: e.isRequired,
            })),
          }
        )

        setState(prev => ({
          ...prev,
          framework: response.framework,
          saving: false,
          error: null,
        }))

        return { success: true }
      } catch (err) {
        const error = err instanceof Error ? err.message : 'Failed to update phase config'
        setState(prev => ({ ...prev, saving: false, error }))
        return { success: false, error }
      }
    },
    [accountId, state.framework]
  )

  // Get configured items for a specific phase
  const getPhaseConfig = useCallback(
    (phaseId: string): { artifacts: PhaseArtifact[]; entities: PhaseEntity[] } | null => {
      if (!state.framework) return null

      const phase = state.framework.config.phases.find((p: JourneyPhase) => p.id === phaseId)
      if (!phase) return null

      return {
        artifacts: phase.artifacts,
        entities: phase.entities,
      }
    },
    [state.framework]
  )

  // Get available items not yet configured in a phase
  const getAvailableItems = useCallback(
    (phaseId: string): { artifacts: ConfigurableItem[]; entities: ConfigurableItem[] } => {
      if (!state.configurableItems || !state.framework) {
        return { artifacts: [], entities: [] }
      }

      const phase = state.framework.config.phases.find((p: JourneyPhase) => p.id === phaseId)
      if (!phase) {
        return state.configurableItems
      }

      // Filter out already-configured items
      const configuredArtifactIds = new Set(phase.artifacts.map(a => a.id))
      const configuredEntityUris = new Set(phase.entities.map(e => e.classUri))

      return {
        artifacts: state.configurableItems.artifacts.filter(a => !configuredArtifactIds.has(a.id)),
        entities: state.configurableItems.entities.filter(e => !configuredEntityUris.has(e.classUri || '')),
      }
    },
    [state.configurableItems, state.framework]
  )

  // Refresh data from server
  const refresh = useCallback(() => {
    return loadData()
  }, [loadData])

  // Load on mount and when accountId changes
  useEffect(() => {
    loadData()
  }, [loadData])

  return {
    // State
    framework: state.framework,
    phases: state.framework?.config.phases ?? [],
    configurableItems: state.configurableItems,
    loading: state.loading,
    saving: state.saving,
    error: state.error,

    // Actions
    updatePhaseConfig,
    refresh,

    // Helpers
    getPhaseConfig,
    getAvailableItems,
  }
}
